import { useMemo } from 'react'
import { ArrowLeft, Printer } from 'lucide-react'
import { PrintAgentBadge } from '@/components/features/tasks/PrintAgentBadge'
import { Button } from '@/components/ui/button'
import { usePrintBoxWithWait } from '@/hooks/usePrintBoxWithWait'
import { isLocalPrintAgentOnline, useLocalPrintAgent } from '@/hooks/useLocalPrintAgent'
import type { TaskBoxRow } from '@/types'

interface PalletWorkPanelProps {
  taskUuid: string
  palletNum: number
  boxes: TaskBoxRow[]
  onBack: () => void
  onRelease: () => void
  isReleasing: boolean
}

export function PalletWorkPanel({
  taskUuid,
  palletNum,
  boxes,
  onBack,
  onRelease,
  isReleasing,
}: PalletWorkPanelProps) {
  const { printNextBox, printPalletKa, isPrinting } = usePrintBoxWithWait(taskUuid)
  const { data: localAgent } = useLocalPrintAgent()
  const agentOnline = isLocalPrintAgentOnline(localAgent)

  const printedCount = boxes.filter(b => b.printed).length
  const nextBox = useMemo(() => boxes.find(b => !b.printed), [boxes])
  const allBoxesDone = boxes.length > 0 && printedCount === boxes.length

  const handlePrint = () => {
    if (!nextBox || isPrinting) return
    void printNextBox(nextBox.num)
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ArrowLeft className="w-4 h-4 mr-1" />
            К паллетам
          </Button>
          <h2 className="text-xl font-bold text-gray-900">Паллета №{palletNum}</h2>
        </div>
        <PrintAgentBadge />
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <p className="text-sm text-gray-500 mb-1">Прогресс коробок</p>
        <p className="text-3xl font-bold text-gray-900">
          {printedCount} <span className="text-lg font-normal text-gray-400">/ {boxes.length}</span>
        </p>
        {nextBox ? (
          <p className="text-sm text-gray-600 mt-2">Следующая: коробка №{nextBox.num}</p>
        ) : allBoxesDone ? (
          <p className="text-sm text-green-700 mt-2">Все коробки напечатаны</p>
        ) : null}
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <Button
          size="lg"
          className="flex-1 h-14 text-base"
          disabled={!nextBox || isPrinting || !agentOnline}
          isLoading={isPrinting}
          onClick={handlePrint}
        >
          <Printer className="w-5 h-5 mr-2" />
          {isPrinting ? 'Печать…' : 'Печать следующей коробки'}
        </Button>
      </div>

      {allBoxesDone && (
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <Button
            variant="secondary"
            disabled={isPrinting || !agentOnline}
            onClick={() => void printPalletKa(palletNum)}
          >
            Печать КА паллеты
          </Button>
        </div>
      )}

      <Button variant="ghost" onClick={onRelease} isLoading={isReleasing} disabled={isPrinting}>
        Освободить паллету
      </Button>
    </div>
  )
}
